import {
  Box,
  Chip,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import dayjs from "dayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { colors, dataGridStyle } from "../../../../theme";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import API from "../../../../api";
import AssetToolBar from "../../../../components/AssetToolBar";
import {
  startToast,
  updateToastDismiss,
  updateToastError,
  updateToastSuccess,
} from "../../../../utils/notification-loading";
import {
  mapAssetRows, applyFilters, vulnSeverityTier, severityStyleForTier,
  severityIconForTier, worstVulnerability, allVulnerabilitiesForAsset,
  hostnameAlternatives, osIcon, osReleaseLabel,
} from "./assetUtils";
import { resolveClassification, classificationKey } from "./assetClassification";
import AssetRiskChip from "./AssetRiskChip";
import AssetsKpiBar from "./AssetsKpiBar";
import { useOrion } from "../../../../contexts/OrionContext";

const Dash = () => <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;

const fmtDate = (v) => {
  if (!v) return null;
  const d = dayjs(v?.$date ?? v);
  return d.isValid() ? d : null;
};

// Colonne "di analisi" nascondibili in blocco col toggle RISK VIEW.
// TYPE è inclusa perché la classificazione del device è parte della lettura
// di rischio/postura tanto quanto OS/CVE/RISK, non della semplice inventory.
const RISK_RELATED_FIELDS = ["device_type", "os_family", "dynamic_risk_score", "vulnerabilities"];

// ── main component ────────────────────────────────────────────────────────────
// `initialFilters` (optional): an array of {field, operator, value} filters (AND'd
// together) to seed the grid with on mount — used by the Overview tab's drill-down
// tiles/charts, which can now stack multiple dimensions (zone + vlan + ...). Read
// once at mount (the grid remounts fresh every time its tab is re-activated, so
// this is never stale).
const HostsOnlineGrid = ({ initialFilters, onOpenAsset } = {}) => {
  // Sorgente unica di verità per la visibilità Orion (Sidebar, AssetToolBar,
  // route guard di /user/network-status/zones e il pulsante ORION -- ora
  // renderizzato dalla pagina, non più da questo componente -- la scrivono/
  // leggono tutti da qui, così restano sempre sincronizzati).
  const { orionEnabled } = useOrion();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModel, setFilterModel] = useState(() => initialFilters ?? []);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [selectedLecs, setSelectedLecs] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Default sort: highest-risk assets first when Orion (and therefore
  // dynamic_risk_score) is available, else the RISK column is hidden and
  // empty, so falls back to status (online first) -- the most useful ordering
  // with only inventory data.
  // Only a default: tracked via the ref below so a toggle of Orion never
  // clobbers a sort the user picked by hand.
  const [sortModel, setSortModel] = useState(() => [{ field: orionEnabled ? "dynamic_risk_score" : "status", sort: "desc" }]);
  const sortModelIsDefault = useRef(true);
  const handleSortModelChange = (model) => {
    sortModelIsDefault.current = false;
    setSortModel(model);
  };
  useEffect(() => {
    if (sortModelIsDefault.current) {
      setSortModel([{ field: orionEnabled ? "dynamic_risk_score" : "status", sort: "desc" }]);
    }
  }, [orionEnabled]);

  // Le colonne "di analisi" (OS, CVE, RISK, TYPE) seguono orionEnabled --
  // nessuno stato locale duplicato, altrimenti un toggle fatto dal pulsante
  // ORION su un'altra pagina (Network Overview) non si rifletterebbe qui
  // finché il componente non viene rimontato.
  useEffect(() => {
    setColumnVisibilityModel((cvm) => ({
      ...cvm,
      ...Object.fromEntries(RISK_RELATED_FIELDS.map((f) => [f, orionEnabled])),
    }));
  }, [orionEnabled]);

  // zones (manually-managed asset grouping) — only the filtering data (zone_name/
  // zone_risk) is still needed here; bulk assignment moved out of Inventory entirely.
  const [zones, setZones] = useState([]);

  const loadZones = useCallback(() => {
    API.getZones().then((res) => {
      if (res.data?.success) setZones(res.data.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadZones(); }, [loadZones]);

  const zoneById = useMemo(
    () => Object.fromEntries(zones.map((z) => [z._id ?? z.id, z])),
    [zones]
  );

  const filteredRows = useMemo(() => {
    // Zone name/risk live on the referenced Zone doc, not the asset row itself.
    // The Zone column was moved to the asset's detail page, but resolving them here
    // is still needed: filters and the Overview's drill-downs match on
    // zone_name/zone_risk like any other field.
    const withZoneInfo = rows.map((r) => {
      const zone = zoneById[r.zone_id];
      return { ...r, zone_name: zone?.name ?? "", zone_risk: zone?.risk ?? "none" };
    });
    return applyFilters(withZoneInfo, filterModel);
  }, [rows, filterModel, zoneById]);

  const loadHosts = useCallback(() => {
    setLoading(true);
    startToast();
    API.getRecentHost({serials: JSON.stringify(selectedLecs), all: true, orion: orionEnabled})
      .then((response) => {
        const actualData = response.data || response;
        if (actualData?.success) {
          setRows(mapAssetRows(actualData.data));
          updateToastDismiss();
        }
      })
      .catch(() => updateToastError("Error"))
      .finally(() => setLoading(false));
  }, [selectedLecs, orionEnabled]);

  useEffect(() => { loadHosts(); }, [loadHosts]);

  // ── handlers — use row OID (row.id), not IP ─────────────────────────────────
  const handleSetPref = (oid) => {
    API.setPref(oid)
      .then((res) => { if (res.data.success) loadHosts(); })
      .catch((err) => console.error("[handleSetPref] Error toggling preferred:", err));
  };


  // ── columns ──────────────────────────────────────────────────────────────────
  // Only the columns you actually scan an inventory by, grouped so the row reads
  // left to right as three questions in order: is it alive → what is it → should I
  // worry. Everything else the document carries (OS, firmware, custom name, zone,
  // exposed ports, service tags, SNMP/DHCP, fingerprint, address history) lives on
  // the asset's detail page, where there is room to show it with its provenance
  // instead of a truncated chip.
  const assetColumns = [
    {
      field: "__info",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="View asset details">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onOpenAsset?.(params.row.id); }}
            sx={{
              color: colors.grey[100],
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "pref",
      headerName: "PREF",
      minWidth: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={params.value === 1 ? "Remove from favourite" : "Set as favourite"}>
          <IconButton
            size="small"
            onClick={() => handleSetPref(params.row.id)}
            sx={{ "&:hover": { backgroundColor: "rgba(255,215,0,0.1)" } }}
          >
            {params.value === 1
              ? <StarIcon sx={{ color: "#FFD700" }} />
              : <StarBorderIcon sx={{ color: colors.grey[400] }} />
            }
          </IconButton>
        </Tooltip>
      ),
    },

    // ── Is it alive ──────────────────────────────────────────────────────────
    {
      field: "status",
      headerName: "STATUS",
      minWidth: 95,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value == null)
          return <Tooltip title="Unknown"><HelpOutlineIcon sx={{ color: "#777" }} /></Tooltip>;
        const online = params.value === "online";
        return (
          <Chip
            label={params.value.toUpperCase()}
            size="small"
            sx={{
              bgcolor: online ? "#4cceac22" : "#ff444422",
              color:   online ? "#4cceac"   : "#ff6644",
              fontSize: 10, height: 20, fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      // Sits with STATUS rather than at the far end of the row: both answer the
      // same question, and "offline" means something different at two minutes than
      // at two weeks.
      field: "last_seen",
      headerName: "LAST SEEN",
      minWidth: 150,
      renderCell: (params) => {
        const d = fmtDate(params.value);
        if (!d) return <Dash />;
        return (
          <Tooltip title={d.format("YYYY-MM-DD HH:mm:ss")}>
            <Typography sx={{ fontSize: 12 }}>{d.format("YYYY-MM-DD HH:mm")}</Typography>
          </Tooltip>
        );
      },
    },

    // ── What is it ───────────────────────────────────────────────────────────
    {
      // Shape comes from the most specific classification level present, in the
      // neutral foreground colour — the taxonomy itself is never colour-coded.
      //
      // The one exception is `is_gateway`: the same glyph turns amber. A gateway
      // sees traffic that isn't its own, which changes how every other fact on the
      // row should be read, so it belongs on the icon that says what the thing is
      // rather than tacked onto the hostname. Tinting keeps both signals — what it
      // is, and that it routes — in the one glyph this column has room for.
      //
      // Part of the "risk view" toggle: classification feeds into how the rest of
      // the row is read, so it's hideable alongside OS/CVE/RISK.
      field: "device_type",
      headerName: "TYPE",
      width: 62,
      align: "center",
      headerAlign: "center",
      valueGetter: (_, row) => classificationKey(row.classification),
      renderCell: (params) => {
        const k = resolveClassification(params.row.classification);
        const Icon = k.icon;
        const gateway = params.row.is_gateway === true;
        const title = (
          <Box>
            <div>{k.path || "Not classified"}</div>
            {k.levels.map((l) => (
              <div key={l.key} style={{ fontSize: 11, opacity: 0.75 }}>
                {l.key.replace(/_/g, " ")}: {l.value}
                {l.confidence != null ? ` · confidence ${l.confidence}` : ""}
              </div>
            ))}
            {k.source?.length > 0 && (
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>based on: {k.source.join(", ")}</div>
            )}
            {gateway && (
              <div style={{ fontSize: 11, color: "#ffb422", marginTop: 4 }}>
                Gateway — routes for other hosts, so its observed traffic is not necessarily its own
              </div>
            )}
          </Box>
        );
        return (
          <Tooltip title={title}>
            <Icon style={{ fontSize: 20, color: gateway ? "#ffb422" : colors.grey[100] }} />
          </Tooltip>
        );
      },
    },
    {
      // Shows the user's custom name when there is one — the dedicated CUSTOM NAME
      // column moved to the detail page's edit form, so this is where a manually
      // assigned name has to surface or it would be invisible in the inventory.
      field: "hostname",
      headerName: "HOSTNAME",
      minWidth: 200,
      flex: 1,
      hideable: false,
      valueGetter: (_, row) => row.machine_name || row.hostname || "",
      renderCell: (params) => {
        if (!params.value) return <Dash />;
        const custom = Boolean(params.row.machine_name);
        const alternatives = hostnameAlternatives(params.row.host_names, params.row.hostname_source);
        const title = [
          custom ? "Custom name set on this asset" : null,
          params.row.hostname_source_label ? `Observed name from ${params.row.hostname_source_label}: ${params.row.hostname}` : null,
          ...alternatives.map((a) => `${a.label}: ${a.name}`),
        ].filter(Boolean).join(" · ");
        return (
          <Tooltip title={title}>
            <Box sx={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {params.value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "ip",
      headerName: "IP ADDRESS",
      minWidth: 145,
      hideable: false,
      renderCell: (params) => (
        <Box sx={{ py: 0.4, minWidth: 0 }}>
          <Box sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 12.5 }}>{params.value || "—"}</Box>
          {params.row.ip_v6 && (
            <Tooltip title={params.row.ip_v6}>
              <Typography sx={{ fontFamily: "monospace", fontSize: 10, color: colors.grey[500], lineHeight: 1.2 }} noWrap>
                {params.row.ip_v6}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "mac",
      headerName: "MAC",
      minWidth: 155,
      renderCell: (params) => {
        if (!params.value) return <Dash />;
        const alt = params.row.alt_macs || [];
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Box sx={{ fontFamily: "monospace", fontSize: 12 }}>{params.value}</Box>
            {alt.length > 0 && (
              <Tooltip title={`Also seen with: ${alt.join(", ")}`}>
                <Chip label={`+${alt.length}`} size="small" sx={{ bgcolor: "#ffffff11", color: colors.grey[400], fontSize: 9.5, height: 16 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "vendor",
      headerName: "VENDOR",
      minWidth: 160,
      renderCell: (params) => {
        if (!params.value) return <Dash />;
        const src = params.row.vendor_source;
        const explain =
          src === "oui" ? "Inferred from the MAC address prefix"
            : src === "device-identity" ? "Declared by the device itself — more reliable than the OUI lookup"
              : src ? `Source: ${src}` : "";
        return (
          <Tooltip title={explain}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5 }} noWrap>{params.value}</Typography>
              {src === "device-identity" && (
                <Chip label="declared" size="small" sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontSize: 9, height: 15, fontWeight: 700 }} />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      // Family on top, commercial release underneath — `os.version` holds the
      // commercial release name ("Windows 11 Pro"), `os.build` the build number.
      // Confidence rides along in the tooltip for all three: 90 and 25 are not the
      // same claim, and the column would otherwise present a guess as a fact.
      //
      // Part of the "risk view" toggle (OS/CVE/RISK/TYPE).
      field: "os_family",
      headerName: "OS",
      minWidth: 200,
      valueGetter: (_, row) => row.os?.family || "",
      renderCell: (params) => {
        const os = params.row.os;
        if (!os?.family && !os?.version && !os?.distribution) return <Dash />;
        const Icon = osIcon(os);
        const title = (
          <Box>
            {os.family && <div>Family: {os.family}{os.family_confidence != null ? ` · confidence ${os.family_confidence}` : ""}{os.family_source ? ` · ${os.family_source}` : ""}</div>}
            {os.distribution && <div>Distribution: {os.distribution}{os.distribution_confidence != null ? ` · confidence ${os.distribution_confidence}` : ""}{os.distribution_source ? ` · ${os.distribution_source}` : ""}</div>}
            {os.version && <div>Release: {os.version}{os.version_confidence != null ? ` · confidence ${os.version_confidence}` : ""}{os.version_source ? ` · ${os.version_source}` : ""}</div>}
            {os.build && <div>Build: {os.build}{os.build_confidence != null ? ` · confidence ${os.build_confidence}` : ""}{os.build_source ? ` · ${os.build_source}` : ""}</div>}
          </Box>
        );
        // Distro name beats bare family as the headline ("Ubuntu" over "Linux"),
        // and the release line combines distro + version ("Ubuntu 22.04") instead
        // of showing the bare release number alone.
        const primaryLabel = os.distribution || os.family || os.version;
        const releaseLabel = osReleaseLabel(os);
        return (
          <Tooltip title={title}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, color: colors.grey[200], minWidth: 0 }}>
              {Icon
                ? <Icon style={{ fontSize: 19, flexShrink: 0 }} />
                : <HelpOutlineIcon sx={{ color: colors.grey[500], fontSize: 17, flexShrink: 0 }} />}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, lineHeight: 1.2 }} noWrap>{primaryLabel}</Typography>
                {releaseLabel && releaseLabel !== primaryLabel && (
                  <Typography sx={{ fontSize: 10, color: colors.grey[500], lineHeight: 1.2 }} noWrap>
                    {releaseLabel}
                  </Typography>
                )}
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "vlan",
      headerName: "VLAN",
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value == null && params.row.inner_vlan == null) return <Dash />;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            {params.value != null && (
              <Chip label={params.value} size="small" variant="outlined" sx={{ fontWeight: "bold", fontSize: 11 }} />
            )}
            {params.row.inner_vlan != null && (
              <Tooltip title={`Inner VLAN ${params.row.inner_vlan} (QinQ)`}>
                <Chip label={params.row.inner_vlan} size="small" sx={{ bgcolor: "#ffffff11", color: colors.grey[400], fontSize: 10, height: 18 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },

    // ── Should I worry ───────────────────────────────────────────────────────
    {
      // Sorts on the raw score even though only the band is drawn, so ordering
      // stays meaningful inside a band.
      //
      // Part of the "risk view" toggle (OS/CVE/RISK/TYPE).
      field: "dynamic_risk_score",
      headerName: "RISK",
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        typeof params.value === "number"
          ? <AssetRiskChip score={params.value} factors={params.row.dynamic_risk_factors} />
          : <Dash />,
    },
    {
      // Part of the "risk view" toggle (OS/CVE/RISK/TYPE).
      field: "vulnerabilities",
      headerName: "CVES",
      minWidth: 175,
      valueGetter: (_, row) => allVulnerabilitiesForAsset(row),
      sortComparator: (v1, v2) => {
        const rank = (v) => ({ critical: 4, high: 3, medium: 2, low: 1, none: 0 }[vulnSeverityTier(worstVulnerability(v))] ?? 0);
        return rank(v1) - rank(v2);
      },
      renderCell: (params) => {
        const vulns = params.value || [];
        if (vulns.length === 0) return <Dash />;
        const worst = worstVulnerability(vulns);
        const tier = vulnSeverityTier(worst);
        const c = severityStyleForTier(tier);
        const Icon = severityIconForTier(tier);
        return (
          <Tooltip title={`Worst: ${worst?.cve_id ?? "—"}${worst?.matched_on ? ` (matched on ${worst.matched_on})` : ""}`}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, py: 0.5 }}>
              {Icon && <Icon sx={{ color: c.color, fontSize: 22, flexShrink: 0 }} />}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>
                  {c.label.toUpperCase()}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: colors.grey[500], lineHeight: 1.2 }}>
                  {vulns.length} CVE{vulns.length > 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: "80vh", display: "flex", flexDirection: "column" }}>
      {/* ── header: KPI overview ─────────────────────────────────────────── */}
      {orionEnabled && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AssetsKpiBar rows={rows} />
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
      <Box
        height="100%"
        sx={{ width: "111.11%", height: "111.11%", transform: "scale(0.9)", transformOrigin: "top left" }}
      >
        <DataGridPro
          rows={filteredRows}
          columns={assetColumns}
          disableColumnFilter
          getRowId={(row) => row.id}
          pagination
          paginationMode="client"
          pageSizeOptions={[30, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          showToolbar
          loading={loading}
          getRowHeight={() => "auto"}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowClick={(params, event) => {
            // ignore clicks on interactive cells (favourite, detail icon)
            const tag = event.target?.tagName?.toLowerCase();
            if (event.target?.closest?.("button, a, input, .MuiIconButton-root")) return;
            if (tag === "button" || tag === "a" || tag === "input") return;
            onOpenAsset?.(params.row.id);
          }}
          sx={{
            ...dataGridStyle,
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          slots={{ toolbar: AssetToolBar }}
          slotProps={{
            loadingOverlay: { variant: "skeleton" },
            toolbar: {
              filterModel, setFilterModel, selectedLecs, setSelectedLecs,
              onImportDone: loadHosts,
              // Righe filtrate correnti (non solo la pagina visibile, paginationMode
              // è "client") -- l'export riflette il filtro attivo in griglia, non
              // sempre l'intero inventory.
              rows: filteredRows,
            },
          }}
        />
      </Box>
      </Box>
    </Box>
  );
};

export default HostsOnlineGrid;